import { mapValues } from "lodash"
import { computeUpdatedFilters } from "helpers/filters"
import { computeFilteredSegment, computeNonNullSegment, removeUnsupportedBaseFilters } from "helpers/segments"
import { BASE_TYPES } from "helpers/base"
import { Filter, FilterSecurity } from "connected-components/dashboard/context/types"

export interface SegmentFilters {
  filters: object
  normalization: string
}

export function computeSegmentFilterUpdate(filterRows: unknown[], normalization: string, isActive: boolean, canKeepSegment = false): SegmentFilters {
  const { filter }: { filter: object | null } = computeUpdatedFilters(filterRows)
  const cleanedFilter: object | null = isActive && !canKeepSegment ? removeUnsupportedBaseFilters(filter) : filter
  return computeFilteredSegment(cleanedFilter, normalization) ?? computeNonNullSegment(cleanedFilter, normalization)
}

interface ComparePortfolioIdentity {
  id?: string | null
  alias?: string | null
}

interface CompareBaseIdentity {
  type?: string | null
  id?: string | null
  experimentId?: string | null
}

// A null portfolio (not loaded, or stale) must say no: the header effect persists the answer.
export function isBaseExperimentOfColumnPortfolio(base: CompareBaseIdentity | null | undefined, columnType: string | null | undefined, columnId: string | null | undefined, pagePortfolio: ComparePortfolioIdentity | null | undefined): boolean {
  if (!pagePortfolio || !base?.experimentId || base.type !== BASE_TYPES.portfolio || columnType !== BASE_TYPES.portfolio) {
    return false
  }

  const portfolioIdentifiers = [pagePortfolio.id, pagePortfolio.alias].filter((identifier): identifier is string => Boolean(identifier))
  return portfolioIdentifiers.includes(columnId ?? "") && portfolioIdentifiers.includes(base.id ?? "")
}

function buildExperimentSegmentRuleSentence(subject: string, object: string): string {
  return `Only an experiment of ${subject}, under Standalone, can carry ${object}.`
}

export function computeExperimentSegmentTooltip(isExperimentOfOwnPortfolio: boolean, baseName: string | null | undefined): string {
  if (isExperimentOfOwnPortfolio) {
    return "Normalize as Standalone to filter by tags and attributes."
  }

  const rule = buildExperimentSegmentRuleSentence("this portfolio", "tag and attribute filters")
  return baseName ? `Not available against ${baseName}. ${rule}` : rule
}

interface AffectedCompareColumn {
  name: string
  label: string | null
}

export function computeExperimentSegmentToastMessage(affectedColumns: AffectedCompareColumn[]): string {
  const rule = buildExperimentSegmentRuleSentence("the column's own portfolio", "them")
  const columnList = affectedColumns.map(({ name, label }) => (label ? `${name} (${label})` : name)).join(", ")
  const count = affectedColumns.length
  return `Removed tag and attribute filters from ${count} column${count === 1 ? "" : "s"}. ${rule} Affected columns: ${columnList}.`
}

function isStoredSecurityRow(row: unknown): row is { id: FilterSecurity } {
  return typeof row === "object" && row !== null && "id" in row
}

export function computePickerFilterFromSegment(filter: Filter | null): Filter | null {
  const storedSecurities = filter?.securities
  if (!storedSecurities) {
    return filter
  }

  const pickerSecurities = mapValues(storedSecurities, (rows: unknown) =>
    (Array.isArray(rows) ? rows : []).filter(isStoredSecurityRow).map(row => row.id))

  return { ...filter, securities: pickerSecurities as unknown as Filter["securities"] }
}
